/**
 * API Client - Morocco Guide Smile
 * Communicates with PHP backend
 */

const API_BASE = process.env.REACT_APP_API_URL || '/api';

class ApiClient {
  constructor() {
    this.token = localStorage.getItem('mgs_token') || '';

    // Enforce HTTPS in production
    if (process.env.NODE_ENV === 'production' && !API_BASE.startsWith('https://')) {
      console.warn('[ApiClient] API base URL is not using HTTPS in production:', API_BASE);
    }
  }

  /**
   * Sanitizes a string value by trimming whitespace and encoding HTML special characters.
   * Prevents XSS/injection attacks in request payloads.
   */
  sanitizeInput(value) {
    if (typeof value !== 'string') return value;
    return value.trim()
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;');
  }

  /**
   * Recursively sanitizes all string values in an object or array.
   */
  sanitizeBody(data) {
    if (typeof data === 'string') {
      return this.sanitizeInput(data);
    }
    if (Array.isArray(data)) {
      return data.map(item => this.sanitizeBody(item));
    }
    if (data !== null && typeof data === 'object') {
      const sanitized = {};
      for (const key of Object.keys(data)) {
        sanitized[key] = this.sanitizeBody(data[key]);
      }
      return sanitized;
    }
    return data;
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE}/${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`
      },
      credentials: 'include',
      ...options
    };

    if (config.body && typeof config.body === 'object' && !(config.body instanceof FormData)) {
      config.body = JSON.stringify(this.sanitizeBody(config.body));
    }
    if (config.body instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    try {
      const res = await fetch(url, config);

      if (res.status === 429) {
        return { error: 'rate_limited', message: 'Trop de requêtes. Réessayez dans 30 secondes.' };
      }

      const data = await res.json();

      if (res.status === 401) {
        this.token = '';
        localStorage.removeItem('mgs_token');
        localStorage.removeItem('mgs_user');
        setTimeout(() => { window.location.href = '/login'; }, 2000);
        return null;
      }

      if (!res.ok) {
        return { error: data.error || 'Erreur serveur' };
      }
      return data;
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('API Error:', e);
      }
      return { error: 'Erreur de connexion' };
    }
  }

  // Auth
  async login(phone, password) {
    const data = await this.request('auth.php?action=login', {
      method: 'POST', body: { phone, password }
    });
    if (data && data.token) {
      this.token = data.token;
      localStorage.setItem('mgs_token', data.token);
      localStorage.setItem('mgs_user', JSON.stringify(data.user));
    }
    return data;
  }

  async logout() {
    await this.request('auth.php?action=logout', { method: 'POST' });
    this.token = '';
    localStorage.removeItem('mgs_token');
    localStorage.removeItem('mgs_user');
  }

  async me() {
    return await this.request('auth.php?action=me');
  }

  // Groups
  async importGroups(data, date) {
    return await this.request('groups.php?action=import', { method: 'POST', body: { data, date } });
  }

  async getGroups(date) {
    return await this.request(`groups.php?action=list&date=${date}`);
  }

  async getMyGroups(date) {
    return await this.request(`groups.php?action=my&date=${date}`);
  }

  async assignGuide(groupId, guideId) {
    return await this.request('groups.php?action=assign', {
      method: 'PUT', body: { group_id: groupId, guide_id: guideId }
    });
  }

  async getSwimmingBookings(date) {
    return await this.request(`groups.php?action=swimming&date=${date}`);
  }

  // Meeting Points
  async addMeetingPoint(groupId, name) {
    return await this.request('meeting-points.php?action=add', {
      method: 'POST', body: { group_id: groupId, name }
    });
  }

  async removeMeetingPoint(meetingPointId) {
    return await this.request(`meeting-points.php?action=delete&id=${meetingPointId}`, {
      method: 'DELETE'
    });
  }

  // Bookings
  async searchBooking(ref) {
    return await this.request(`bookings.php?action=search&ref=${encodeURIComponent(ref)}`);
  }

  // Guides
  async getGuides() {
    return await this.request('guides.php?action=list');
  }

  async addGuide(name, phone, role) {
    return await this.request('guides.php?action=add', { method: 'POST', body: { name, phone, role } });
  }

  async toggleGuide(id) {
    return await this.request(`guides.php?action=toggle&id=${id}`, { method: 'PUT' });
  }

  async deleteGuide(id) {
    return await this.request(`guides.php?action=delete&id=${id}`, { method: 'DELETE' });
  }

  // Attendance
  async reportAttendance(bookingId, status, reason) {
    return await this.request('attendance.php?action=report', {
      method: 'POST', body: { booking_id: bookingId, status, reason }
    });
  }

  // Geo
  async geoCheckin(latitude, longitude) {
    return await this.request('geo.php?action=checkin', {
      method: 'POST', body: { latitude, longitude }
    });
  }

  async geoStatus() {
    return await this.request('geo.php?action=status');
  }

  // Stats
  async getStats(month) {
    return await this.request(`stats.php?action=overview&month=${month}`);
  }

  async getMyStats(month) {
    return await this.request(`stats.php?action=my&month=${month}`);
  }

  // Change password
  async changePassword(oldPassword, newPassword) {
    return await this.request('auth.php?action=change-password', {
      method: 'POST', body: { old_password: oldPassword, new_password: newPassword }
    });
  }

  // Extras (financial)
  async addExtra(guideId, amount, description, bookingRef, date) {
    return await this.request('extras.php?action=add', {
      method: 'POST', body: { guide_id: guideId, amount, description, booking_ref: bookingRef, date }
    });
  }

  async getExtras(date) {
    return await this.request(`extras.php?action=list&date=${date}`);
  }

  async getExtrasMonth(month) {
    return await this.request(`extras.php?action=list&month=${month}`);
  }

  async deleteExtra(id) {
    return await this.request(`extras.php?action=delete&id=${id}`, { method: 'DELETE' });
  }
}

const api = new ApiClient();
export default api;
